import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Avatar,
  IconButton,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  TextField,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useState, useEffect } from 'react';
import AdminUserList from '../../../utilities/api/user/AdminUserList';
import AdminUserDetail from '../../../utilities/api/user/AdminUserDetail';
import { useSelector } from 'react-redux';
import { ImageBaseURL } from '../../../utilities/api/axios';
import UserExport from './UserExport';

const positionLabels = {
  '-1': 'None (Student)',
  0: 'TL student management body',
  1: 'Manager (other technical clubs)',
  2: 'TL Manager',
  3: 'TL Technician',
  4: 'Other',
  5: 'Professor In Charge',
};

const User = ({ searchQuery }) => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [roleDialogUser, setRoleDialogUser] = useState(null);
  const [selectedRoleValue, setSelectedRoleValue] = useState('');
  const [otherRoleText, setOtherRoleText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const user = useSelector(state => state.user.user);
  const currentUserCollegeName = user?.user?.TL_name;

  const handleOpenDialog = (userId, currentStatus, statusType, userName) => {
    setSelectedUser({
      userId,
      newStatusValue: !currentStatus,
      statusType,
      userName,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleConfirmStatusToggle = async () => {
    if (selectedUser) {
      const { userId, newStatusValue, statusType } = selectedUser;
      const payload =
        statusType === 'admin'
          ? { is_admin: newStatusValue }
          : { is_student: newStatusValue };
      const updatedField = statusType === 'admin' ? 'isAdmin' : 'isStudent';
      try {
        await AdminUserDetail.AdminDetailUpdate(userId, payload);
        const updateFn = prevRows =>
          prevRows.map(row =>
            row.id === userId ? { ...row, [updatedField]: newStatusValue } : row
          );
        setRows(updateFn);
        setFilteredRows(updateFn);
      } catch (error) {
        console.error(`Error updating ${statusType} status:`, error);
      }
      handleCloseDialog();
    }
  };

  const handleOpenRoleDialog = userData => {
    setRoleDialogUser(userData);
    const defaultValue =
      !userData.isAdmin && userData.isStudent && !userData.position
        ? '-1'
        : userData.position || '';
    setSelectedRoleValue(defaultValue);
    setOtherRoleText(userData.other_position || '');
    setIsRoleDialogOpen(true);
  };

  const handleCloseRoleDialog = () => {
    setIsRoleDialogOpen(false);
    setRoleDialogUser(null);
    setSelectedRoleValue('');
    setOtherRoleText('');
  };

  const handleConfirmRoleChange = async () => {
    if (!roleDialogUser) return;
    const payload =
      selectedRoleValue === '-1'
        ? { position: '', other_position: '' }
        : {
            position: selectedRoleValue,
            other_position: selectedRoleValue === '4' ? otherRoleText : '',
          };
    try {
      await AdminUserDetail.AdminDetailUpdate(roleDialogUser.id, payload);
      const updateFn = prevRows =>
        prevRows.map(row =>
          row.id === roleDialogUser.id
            ? {
                ...row,
                position: payload.position,
                other_position: payload.other_position,
              }
            : row
        );
      setRows(updateFn);
      setFilteredRows(updateFn);
    } catch (error) {
      console.error('Error updating user role:', error);
    }
    handleCloseRoleDialog();
  };

  const handleOpenDeleteDialog = (userId, userName) => {
    setUserToDelete({ id: userId, name: userName });
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await AdminUserDetail.AdminDetailDelete(userToDelete.id);
      const updated = rows.filter(row => row.id !== userToDelete.id);
      setRows(updated);
      setFilteredRows(updated);
      setNotification({
        open: true,
        message: `User ${userToDelete.name} deleted.`,
        severity: 'success',
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || error.message,
        severity: 'error',
      });
    }
    handleCloseDeleteDialog();
  };

  const handleCloseNotification = () =>
    setNotification(prev => ({ ...prev, open: false }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await AdminUserList.AllUserListGet();
        const formatted = data
          .filter(user => user.TL_name === currentUserCollegeName)
          .map(user => ({
            id: user.id,
            username: user.username,
            name: user.name || user.username,
            email: user.email,
            group: user.TL_name === 'MBF Admin' ? 'MBF Admin' : user.TL_name,
            image: user.profile_pic,
            isAdmin: user.is_admin,
            isStudent: user.is_student,
            position: user.position,
            other_position: user.other_position || '',
            current_year: user.current_year || 'N/A',
            degree: user.degree || 'N/A',
            department: user.department || 'N/A',
            graduation_year: user.graduation_year || 'N/A',
            phone_number: user.phone_number || '',
            created: user.created || '',
          }));
        setRows(formatted);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [user, currentUserCollegeName]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredRows(rows);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredRows(
        rows.filter(
          row =>
            (row.name && row.name.toLowerCase().includes(query)) ||
            (row.username && row.username.toLowerCase().includes(query)) ||
            (row.email && row.email.toLowerCase().includes(query))
        )
      );
    }
  }, [searchQuery, rows]);

  const columns = [
    {
      field: 'name',
      headerName: 'User Name',
      flex: 1.5,
      minWidth: 200,
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={`${ImageBaseURL}${params.row.image}`}
            alt={params.row.name}
          />
          <Box>
            <Typography>{params.value}</Typography>
            <Typography variant="caption" color="textSecondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1.2,
      minWidth: 180,
      renderCell: params => {
        let displayRole = 'N/A';
        if (
          !params.row.isAdmin &&
          params.row.isStudent &&
          (!params.row.position || params.row.position === '-1')
        ) {
          displayRole = 'Student';
        } else if (params.row.position === '-1') {
          displayRole = 'None';
        } else {
          const base = positionLabels[params.row.position] || 'N/A';
          displayRole =
            params.row.position === '4' && params.row.other_position
              ? `${base} (${params.row.other_position})`
              : base;
        }
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayRole}
            </Typography>
            <Tooltip title="Edit Role">
              <IconButton
                size="small"
                onClick={() => handleOpenRoleDialog(params.row)}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'isAdmin',
      headerName: 'Admin',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <Checkbox
          checked={params.value}
          onChange={() =>
            handleOpenDialog(
              params.row.id,
              params.value,
              'admin',
              params.row.name
            )
          }
        />
      ),
    },
    {
      field: 'isStudent',
      headerName: 'Student',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <Checkbox
          checked={params.value}
          onChange={() =>
            handleOpenDialog(
              params.row.id,
              params.value,
              'student',
              params.row.name
            )
          }
        />
      ),
    },
    {
      field: 'group',
      headerName: 'Group Name',
      flex: 0.8,
      minWidth: 120,
      renderCell: params => (
        <Tooltip title={params.value} placement="top">
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.5,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <Tooltip title="Delete User">
          <IconButton
            onClick={() => handleOpenDeleteDialog(params.id, params.row.name)}
          >
            <DeleteIcon color="error" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <UserExport
          users={filteredRows}
          currentUserCollegeName={currentUserCollegeName}
        />
      </Box>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[5, 10, 20]}
        checkboxSelection
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-cell': {
            padding: '12px 16px',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f9f9f9',
          },
          boxShadow: 1,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      />

      {/* Dialogs */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          Are you sure you want to{' '}
          <b>{selectedUser?.newStatusValue ? 'grant' : 'remove'}</b>{' '}
          <b>{selectedUser?.statusType}</b> rights for{' '}
          <strong>{selectedUser?.userName}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmStatusToggle} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRoleDialogOpen}
        onClose={handleCloseRoleDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Change Role for {roleDialogUser?.name}</DialogTitle>
        <DialogContent>
          <Select
            fullWidth
            value={selectedRoleValue}
            onChange={e => setSelectedRoleValue(e.target.value)}
            sx={{ mb: 2 }}
          >
            {Object.entries(positionLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
          {selectedRoleValue === '4' && (
            <TextField
              fullWidth
              label="Specify Other Position"
              value={otherRoleText}
              onChange={e => setOtherRoleText(e.target.value)}
              variant="outlined"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoleDialog}>Cancel</Button>
          <Button onClick={handleConfirmRoleChange} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ bgcolor: '#ffebee', color: 'error.main' }}>
          Warning: Delete User
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{userToDelete?.name}</strong>?
          </Typography>
          <Typography color="error" fontWeight="bold" sx={{ mt: 2 }}>
            Warning: This action cannot be undone!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default User;
